import { QueryClient } from 'react-query';

import {
  VisualisationPendingChanges,
  PendingChange,
  PendingCreateChange,
  PendingUpdateChange,
  PendingDeleteChange,
} from './visualisationPendingChanges';
import { defaultHeaders } from '../constants';
import { APIBaseChronos } from '@/api/hosts';
import { QueryEntity } from '@/api/types';
import { FetchMethod } from '@/api/useGetFetchConfig';
import { Classification, Relevance } from '@/types';

export interface SaveVisualisationChangesOptions {
  caseId: string;
  chronologyId: string;
  getAccessToken: () => Promise<string | undefined>;
  queryClient: QueryClient;
  onComplete: () => void;
  pendingChanges: VisualisationPendingChanges;
}

export async function saveVisualisationChanges(options: SaveVisualisationChangesOptions) {
  const changes = options.pendingChanges.getAllChanges();

  if (changes.length === 0) {
    options.onComplete();
    return;
  }

  // Filter out annotation changes since they don't need database sync
  const nodesToSync = changes.filter((change) => change.type !== 'annotation');
  const annotationChanges = changes.filter((change) => change.type === 'annotation');

  // Process node changes (create, update, delete)
  for (let i = 0; i < nodesToSync.length; i++) {
    const change = nodesToSync[i];

    try {
      await processChange(change, options);
      options.pendingChanges.removeChange(change.id);
    } catch (error) {
      console.error(`Failed to process change ${change.id}:`, error);
    }
  }

  // Clear annotation changes (they're saved in visualisation but don't need database sync)
  annotationChanges.forEach((change) => {
    options.pendingChanges.removeChange(change.id);
  });

  // Invalidate chronology query to refresh the table data
  options.queryClient.invalidateQueries([QueryEntity.Chronology, { chronologyId: options.chronologyId }]);

  options.onComplete();
}

async function processChange(change: PendingChange, options: SaveVisualisationChangesOptions): Promise<void> {
  switch (change.type) {
    case 'create':
      await processCreateChange(change, options);
      break;
    case 'update':
      await processUpdateChange(change, options);
      break;
    case 'delete':
      await processDeleteChange(change, options);
      break;
    case 'annotation':
      // Annotation changes don't need database sync, they're just for UI indication
      break;
  }
}

async function processCreateChange(
  change: PendingCreateChange,
  options: SaveVisualisationChangesOptions,
): Promise<void> {
  const { caseId, chronologyId, getAccessToken } = options;
  const { id } = change;
  const { description, factDateText, docId } = change.data;

  const accessToken = await getAccessToken();
  if (!accessToken) {
    throw new Error('No access token available');
  }

  // Create fact associated with a document
  const url = `${APIBaseChronos}/api/case/${caseId}/chronology/${chronologyId}/doc/${docId}/add-fact`;
  const data = { description, factDateText, relevance: Relevance.Relevant, classification: Classification.Primary, id };

  const response = await fetch(url, {
    method: FetchMethod.POST,
    ...defaultHeaders(accessToken),
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    throw new Error(`Failed to create fact: ${response.statusText}`);
  }
}

async function processUpdateChange(
  change: PendingUpdateChange,
  options: SaveVisualisationChangesOptions,
): Promise<void> {
  const { caseId, chronologyId, getAccessToken } = options;
  const { id, data } = change;

  const accessToken = await getAccessToken();
  if (!accessToken) {
    throw new Error('No access token available');
  }

  // Process each field update separately (following the existing pattern)
  for (const [field, value] of Object.entries(data)) {
    if (value !== undefined) {
      const response = await fetch(`${APIBaseChronos}/api/case/${caseId}/chronology/${chronologyId}/fact/${id}`, {
        method: FetchMethod.PATCH,
        ...defaultHeaders(accessToken),
        body: JSON.stringify({ [field]: value }),
      });

      if (!response.ok) {
        throw new Error(`Failed to update fact field ${field}: ${response.statusText}`);
      }
    }
  }
}

async function processDeleteChange(
  change: PendingDeleteChange,
  options: SaveVisualisationChangesOptions,
): Promise<void> {
  const { caseId, getAccessToken } = options;
  const { id } = change;

  const accessToken = await getAccessToken();
  if (!accessToken) {
    throw new Error('No access token available');
  }

  const response = await fetch(`${APIBaseChronos}/api/case/${caseId}/chronology/fact/${id}`, {
    method: FetchMethod.DELETE,
    ...defaultHeaders(accessToken),
  });

  if (!response.ok) {
    throw new Error(`Failed to delete fact: ${response.statusText}`);
  }
}
